import { TBaseItem } from "@centricsoftware/design-system";
import { ISingleSelectOptionStore } from "csi/components/types";
import { ILocaleProvider } from "designSystem/locale";
import { action, makeObservable, observable, runInAction } from "mobx";

class SingleSelectOptionManager {
	/** initial value which may not be in the data store */
	@observable.ref initialValue?: string | TBaseItem;

	/** indicates if querying data store */
	@observable isLoading = false;

	/** indicates if querying data store */
	readonly loc: ILocaleProvider;

	/** options from data store */
	@observable.ref options: TBaseItem[];

	/** function that will transform the data store result to an option to override the default */
	@observable.ref optionStore: ISingleSelectOptionStore;

	/** callback when select is opened */
	@action onOpen = async (): Promise<void> => {
		const { initialValue, loc, options, optionStore } = this;

		// do nothing if options already exist
		if (options || !optionStore) {
			return;
		}

		let selectOptions: TBaseItem[];

		this.isLoading = true;

		try {
			selectOptions = await optionStore.query(loc);

			if (initialValue) {
				// if initial value is not in options, then add an invalid one
				const item = initialValue as TBaseItem;
				const initialId: string = item.id ?? initialValue as string;
				
				if (initialId && !selectOptions.find(({ id }) => id === initialId)) {
					selectOptions.push({
						id: initialId,
						invalid: true,
						name: item.name || initialId,
					});
				}
			}
		} catch (ex) {
			selectOptions = [];
		}
		
		runInAction(() => {
			this.isLoading = false;
			this.options = selectOptions;
		});
	};
	
	/** reset options store */
	@action refresh = (optionStore: ISingleSelectOptionStore, initialValue: string | TBaseItem): void => {
		this.initialValue = initialValue;
		this.options = undefined;
		this.optionStore = optionStore;
	};

	constructor(loc: ILocaleProvider) {
		this.loc = loc;
		makeObservable(this);
	}
}

export default SingleSelectOptionManager;